import { useState, useEffect, useRef } from "react";

export function AirTalentLogo() {
  // Enhanced font families for better visual variety
  const fontFamilies = [
    "font-serif",
    "font-sans",
    "font-mono",
    "font-bold",
    "font-light",
    "font-medium",
    "font-semibold",

    "font-black",
    "font-thin",
    "font-extralight",
  ];

  // Only animate specific letters (excluding the first 'a' and 'l', animate other vowels and some consonants)
  const animatedLetterIndices = [1, 2, 3, 4, 6, 7, 8]; // i, r, t, a, e, n, t

  // Original custom font families for each letter (empty string means use CSS class)
  const originalFonts = ["", "", "", "", "", "", "", "", ""]; // Use CSS classes instead

  // State for shuffling letters
  const [shufflingLetters, setShufflingLetters] = useState<Set<number>>(
    new Set()
  );
  const [currentFonts, setCurrentFonts] = useState<string[]>(originalFonts);
  const currentLetterIndexRef = useRef<number>(0);
  const isAnimatingRef = useRef<boolean>(false);
  const mainIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shuffleIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to get random font
  const getRandomFont = () => {
    return fontFamilies[Math.floor(Math.random() * fontFamilies.length)];
  };

  // Sequential shuffle effect for letters
  useEffect(() => {
    // Reset animation state when component mounts
    currentLetterIndexRef.current = 0;
    isAnimatingRef.current = false;
    setShufflingLetters(new Set());
    setCurrentFonts(originalFonts);
    
    const startAnimation = () => {
      // Prevent multiple animations from starting simultaneously
      if (isAnimatingRef.current) {
        return;
      }
      
      // Clear any existing intervals before starting new ones
      if (mainIntervalRef.current) {
        clearInterval(mainIntervalRef.current);
        mainIntervalRef.current = null;
      }
      if (shuffleIntervalRef.current) {
        clearInterval(shuffleIntervalRef.current);
        shuffleIntervalRef.current = null;
      }
      
      isAnimatingRef.current = true;
      
      mainIntervalRef.current = setInterval(() => {
        // Get the next letter in sequence using ref
        const letterToAnimate =
          animatedLetterIndices[currentLetterIndexRef.current];

        // Start shuffle effect
        setShufflingLetters(new Set([letterToAnimate]));

        // Enhanced font changes with more variety
        let shuffleCount = 0;
        if (shuffleIntervalRef.current) {
          clearInterval(shuffleIntervalRef.current);
          shuffleIntervalRef.current = null;
        }
        shuffleIntervalRef.current = setInterval(() => {
          setCurrentFonts((prev) => {
            const newFonts = [...prev];
            // Ensure we don't pick the same font twice in a row
            let newFont;
            do {
              newFont = getRandomFont();
            } while (
              newFont === newFonts[letterToAnimate] &&
              fontFamilies.length > 1
            );

            newFonts[letterToAnimate] = newFont;
            return newFonts;
          });

          shuffleCount++;
          if (shuffleCount >= 4) {
            // Slightly more shuffles for better effect
            if (shuffleIntervalRef.current) {
              clearInterval(shuffleIntervalRef.current);
              shuffleIntervalRef.current = null;
            }
            setShufflingLetters(new Set());

            // Don't reset - keep the last shuffled font until next animation
            // The letter keeps its new font family until it's animated again

            // Move to next letter in sequence using ref
            currentLetterIndexRef.current =
              (currentLetterIndexRef.current + 1) %
              animatedLetterIndices.length;
          }
        }, 225); // 25% faster (300 * 0.75 = 225)
      }, 1875); // 25% faster (2500 * 0.75 = 1875)
    };

    // Stop current animation properly
    const stopAnimation = () => {
      isAnimatingRef.current = false;
      if (mainIntervalRef.current) {
        clearInterval(mainIntervalRef.current);
        mainIntervalRef.current = null;
      }
      if (shuffleIntervalRef.current) {
        clearInterval(shuffleIntervalRef.current);
        shuffleIntervalRef.current = null;
      }
      setShufflingLetters(new Set());
    };

    // Handle page visibility changes (when user switches tabs/apps)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Stop while hidden to avoid throttled, inconsistent timers
        stopAnimation();
        return;
      }
      // Visible again - debounce restart to avoid duplicate triggers
      stopAnimation();
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      restartTimeoutRef.current = setTimeout(() => {
        currentLetterIndexRef.current = 0;
        setCurrentFonts(originalFonts);
        startAnimation();
      }, 150);
    };

    // Handle window focus (additional safety for browser focus changes)
    const handleWindowFocus = () => {
      // Debounce restart on focus as well
      stopAnimation();
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      restartTimeoutRef.current = setTimeout(() => {
        currentLetterIndexRef.current = 0;
        setCurrentFonts(originalFonts);
        startAnimation();
      }, 150);
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    startAnimation();

    return () => {
      // Stop animation properly
      isAnimatingRef.current = false;
      if (mainIntervalRef.current) {
        clearInterval(mainIntervalRef.current);
        mainIntervalRef.current = null;
      }
      if (shuffleIntervalRef.current) {
        clearInterval(shuffleIntervalRef.current);
        shuffleIntervalRef.current = null;
      }
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      // Remove event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      // Reset animation state on cleanup
      setShufflingLetters(new Set());
      currentLetterIndexRef.current = 0;
    };
  }, []); // Remove currentLetterIndex from dependencies to prevent restarts

  const letters = ["a", "i", "r", "t", "a", "l", "e", "n", "t"];
  const letterClasses = [
    "airtalent-a",
    "airtalent-i",
    "airtalent-r",
    "airtalent-t1",
    "airtalent-a2",
    "airtalent-l",
    "airtalent-e",
    "airtalent-n",
    "airtalent-t2",
  ];

  // Individual widths for each letter to accommodate font variations with improved mobile responsive scaling
  const getLetterWidth = (index: number) => {
    const letterWidths = [
      "xl:w-[180px] md:w-[120px] sm:w-[95px] xs:w-[60px] w-[50px] uppercase", // 'a' - first letter (stylized) - balanced space for large screens
      "xl:min-w-[70px] xl:max-w-[90px] lg:min-w-[60px] lg:max-w-[90px] md:min-w-[50px] md:max-w-[50px] sm:min-w-[38px] sm:max-w-[38px] xs:min-w-[25px] xs:max-w-[25px] min-w-[22px] max-w-[22px]", // 'i' - narrow - balanced space for large screens
      "xl:min-w-[145px] xl:max-w-[145px] lg:min-w-[130px] lg:max-w-[145px] md:min-w-[75px] md:max-w-[75px] sm:min-w-[62px] sm:max-w-[62px] xs:min-w-[40px] xs:max-w-[40px] min-w-[35px] max-w-[35px] uppercase", // 'r' - extra space for large screens
      "xl:min-w-[110px] xl:max-w-[110px] lg:min-w-[90px] lg:max-w-[90px] md:min-w-[65px] md:max-w-[65px] sm:min-w-[52px] sm:max-w-[52px] xs:min-w-[35px] xs:max-w-[35px] min-w-[30px] max-w-[30px]", // 't' - medium - balanced space for large screens
      "xl:min-w-[105px] xl:max-w-[115px] lg:min-w-[100px] lg:max-w-[115px] md:min-w-[75px] md:max-w-[75px] sm:min-w-[62px] sm:max-w-[62px] xs:min-w-[40px] xs:max-w-[40px] min-w-[35px] max-w-[35px]", // 'a' - medium - balanced space for large screens
      "xl:min-w-[165px] xl:max-w-[165px] lg:min-w-[110px] lg:max-w-[145px] md:min-w-[110px] md:max-w-[110px] sm:min-w-[85px] sm:max-w-[85px] xs:min-w-[55px] xs:max-w-[55px] min-w-[50px] max-w-[50px]", // 'l' - balanced space for large screens
      "xl:min-w-[115px] xl:max-w-[120px] lg:min-w-[115px] lg:max-w-[120px] md:min-w-[85px] md:max-w-[85px] sm:min-w-[68px] sm:max-w-[68px] xs:min-w-[42px] xs:max-w-[42px] min-w-[38px] max-w-[38px]", // 'e' - balanced space for large screens
      "xl:min-w-[150px] xl:max-w-[150px] lg:min-w-[110px] lg:max-w-[120px] md:min-w-[90px] md:max-w-[90px] sm:min-w-[72px] sm:max-w-[72px] xs:min-w-[45px] xs:max-w-[45px] min-w-[40px] max-w-[40px]", // 'n' - balanced space for large screens
      "xl:min-w-[90px] xl:max-w-[100px] lg:min-w-[90px] lg:max-w-[100px] md:min-w-[65px] md:max-w-[65px] sm:min-w-[52px] sm:max-w-[52px] xs:min-w-[35px] xs:max-w-[35px] min-w-[30px] max-w-[30px]", // 't' - medium - balanced space for large screens
    ];
    return letterWidths[index];
  };

  return (
    <>
      {letters.map((letter, index) => (
        <span
          key={index}
            className={`
            airtalent-letter ${letterClasses[index]} ${
            currentFonts[index] || ""
          } 
            text-[60px] xs:text-[80px] sm:text-[120px] lg:text-[180px] xl:text-[220px] 
            ${shufflingLetters.has(index) ? "animate-pulse" : ""} 
            ${index === 0 ? "relative" : ""} 
            inline-flex items-center justify-center
            ${getLetterWidth(index)}
            leading-none flex-shrink-0
          `}
        >
          {letter}
        </span>
      ))}
    </>
  );
}
